/**
 * Centralized notification type styling and labels.
 *
 * Used across portal components (ActionItems, NotificationPanel,
 * EmailMonitorDecisionsPage) to ensure consistent notification
 * presentation.
 */

export type NotificationVariant = 'danger' | 'warning' | 'success';

/**
 * Map notification_type values to their visual variant (danger/warning/success).
 *
 * This is the single source of truth for notification type styling.
 */
export const NOTIFICATION_TYPE_VARIANT: Record<string, NotificationVariant> = {
  // Order lifecycle
  order_detected: 'warning',
  order_submitted: 'success',
  order_confirmed: 'success',
  order_cancelled: 'danger',
  // Customer emailed a change to a placed order — amber, review-and-apply.
  order_revised: 'warning',
  // An "Unknown customer" draft was re-linked to the Sage account the desk
  // created after Merlin drafted it — good news, the draft is ready to submit.
  draft_customer_relinked: 'success',
  // Sage items a rep's order waited on now exist; the draft is ready to key (kanban 9c2ab40c).
  draft_items_resolved: 'success',
  // Possible missed backorder — invoice shipped fewer units than we submitted with no
  // backorder. Routed via the critical-notifications channel (CriticalAlertBar).
  order_short_ship: 'danger',
  // Email events
  email_assigned: 'warning',
  // AI email monitoring
  email_follow_up_needed: 'warning',
  email_urgent_request: 'danger',
  email_complaint: 'danger',
  email_new_lead: 'success',
  email_delivery_issue: 'danger',
  email_pricing_request: 'warning',
  email_general_fyi: 'warning',
  email_stale_followup: 'danger',
  // Quality Investigations (Jess's QCP case files)
  investigation_response_arrived: 'success',
  investigation_ask_overdue: 'danger',
  // Sigma SLA clocks on the quality case files (kanban 6c648cab).
  investigation_sla_due_soon: 'warning',
  investigation_sla_missed: 'danger',
  investigation_effectiveness_recurrence: 'warning',
  investigation_effectiveness_held: 'success',
  // A follow-up Merlin promised on the Quality door came back (kanban
  // bf65c1cd / 43160d8b): a kept promise, with the answer on the item.
  merlin_followup_fired: 'success',
  // The daily answer sweep found (or thinks it found) the answer to an open
  // ask elsewhere — a meeting, mail, an order that shipped (kanban 3eed0be5).
  quality_answer_found: 'success',
  // Expired-quote order flow (2026-08-26): order went to Sage at an expired
  // project-quote price. MATCH targets the salesperson; DECISION broadcasts
  // the YES/NO outcome to CSRs (a DENIED means adjust before invoicing).
  expired_quote_match: 'warning',
  expired_quote_decision: 'danger',
  // A scanned BOL the sales site is holding for freight (live carrier quote
  // failed): the CSR has to key the freight before the invoice batch.
  bol_scan_freight_needs_input: 'danger',
  // A scanned BOL waiting on a PRO / pickup number or a ship confirmation:
  // the order does not ship or invoice until the desk answers.
  bol_scan_waiting_on_desk: 'danger',
  // A prepaid order whose Sage invoice posted at $0 freight and nothing
  // else paged it: the desk bills it on a freight-only invoice or credits
  // and rebills (kanban d72e7b54).
  prepaid_freight_not_billed: 'danger',
  // System
  system_alert: 'danger',
};

/** Default variant when the notification type is unknown. */
export const DEFAULT_NOTIFICATION_VARIANT: NotificationVariant = 'warning';

/**
 * Get the variant for a notification type, falling back to default.
 * Case-insensitive lookup (DB stores UPPERCASE, mapping uses lowercase).
 */
export function getNotificationVariant(type: string): NotificationVariant {
  const normalized = type.toLowerCase();
  return NOTIFICATION_TYPE_VARIANT[normalized] ?? DEFAULT_NOTIFICATION_VARIANT;
}

/**
 * Whether the bell shows its small unread DOT: only when there are fresh
 * unread bell-only notifications AND no numeric action-item badge (the
 * number always wins — a dot next to a count would be noise).
 */
export function shouldShowBellDot(
  actionItemCount: number,
  freshUnreadCount: number,
): boolean {
  return actionItemCount === 0 && freshUnreadCount > 0;
}

/**
 * What the bell's unread marker says (proposal 10: a red dot with no count
 * said nothing to a colour-blind reader): "2 new", in the same place and
 * under the same rule as the old dot; null when there is no marker.
 */
export function bellNewLabel(actionItemCount: number, freshUnreadCount: number): string | null {
  if (!shouldShowBellDot(actionItemCount, freshUnreadCount)) return null;
  return `${freshUnreadCount > 99 ? '99+' : freshUnreadCount} new`;
}
